import {
    IonButtons,
    IonContent,
    IonHeader,
    IonImg,
    IonItem,
    IonLabel,
    IonList,
    IonMenuButton,
    IonPage,
    IonSegment,
    IonSegmentButton,
    IonTitle,
    IonToolbar
} from '@ionic/react';
import './Ranglijsten.css';
import {useCallback, useEffect, useMemo, useState} from "react";
import createAxiosInstance from "../../services/AxiosInstance";
import ProfilePicture from "../../components/shared/ProfilePicture";
import LoadingSpinner from "../../components/shared/LoadingSpinner";

type BaseLeaderboardKey = 'attendance' | 'expenses' | 'pukes';
type DertigenLeaderboardKey = 'stripe-goal' | 'doubler' | 'dertigen-record';
type LeaderboardKey = BaseLeaderboardKey | 'dertigen';

interface RankingEntry {
    name: string;
    data: number;
    profilePictureUrl?: string | null;
}

const BASE_ENDPOINTS: Record<BaseLeaderboardKey, string> = {
    attendance: '/user/eveningRankings',
    expenses: '/user/expenseRankings',
    pukes: '/user/pukeRankings',
};

const DERTIGEN_ENDPOINTS: Record<DertigenLeaderboardKey, string> = {
    'stripe-goal': '/user/weeklyTargetRanking',
    doubler: '/user/doublersRanking',
    'dertigen-record': '/user/recordRanking',
};

const INITIAL_BASE_RANKINGS: Record<BaseLeaderboardKey, RankingEntry[]> = {
    attendance: [],
    expenses: [],
    pukes: [],
};

const medalForIndex = (index: number) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return `${index + 1}.`;
};

const normalizeRankings = (raw: unknown): RankingEntry[] => {
    if (!Array.isArray(raw)) return [];

    return raw
        .map((entry: any) => ({
            name: typeof entry?.name === 'string' ? entry.name : 'Onbekend',
            data: Number(entry?.data ?? 0),
            profilePictureUrl: entry?.profilePictureUrl ?? null,
        }))
        .sort((a, b) => b.data - a.data);
};

const Ranglijsten: React.FC = () => {
    const [baseRankings, setBaseRankings] = useState<Record<BaseLeaderboardKey, RankingEntry[]>>(INITIAL_BASE_RANKINGS);
    const [dertigenRankings, setDertigenRankings] = useState<RankingEntry[]>([]);
    const [selectedLeaderboard, setSelectedLeaderboard] = useState<LeaderboardKey>('attendance');
    const [selectedDertigenLeaderboard, setSelectedDertigenLeaderboard] = useState<DertigenLeaderboardKey>('stripe-goal');
    const [baseLoading, setBaseLoading] = useState<boolean>(true);
    const [dertigenLoading, setDertigenLoading] = useState<boolean>(false);
    const [baseError, setBaseError] = useState<string | null>(null);
    const [dertigenError, setDertigenError] = useState<string | null>(null);
    const [loadedDertigen, setLoadedDertigen] = useState<Record<DertigenLeaderboardKey, boolean>>({
        'stripe-goal': false,
        doubler: false,
        'dertigen-record': false,
    });

    const axios = useMemo(() => createAxiosInstance(), []);

    const resolveProfilePicture = useCallback((profilePictureUrl?: string | null) => {
        if (profilePictureUrl) {
            return `${process.env.REACT_APP_STORAGE}${profilePictureUrl}`;
        }
        return `${process.env.REACT_APP_STORAGE}default-profile.jpg`;
    }, []);

    const formatLeaderboardText = useCallback((entry: RankingEntry, board: LeaderboardKey, dertigenBoard: DertigenLeaderboardKey) => {
        if (board === 'attendance') return `${entry.data} dagen aanwezig`;
        if (board === 'expenses') return `€ ${entry.data.toFixed(2)} uitgegeven`;
        if (board === 'pukes') return `${entry.data} keer gekotst`;

        if (dertigenBoard === 'stripe-goal') return `${entry.data.toFixed(2)}% doelen gehaald`;
        if (dertigenBoard === 'doubler') return `${entry.data} verdubbelaars`;
        return `Record: ${entry.data} strepen`;
    }, []);

    const fetchBaseRankings = useCallback(async () => {
        setBaseLoading(true);
        setBaseError(null);

        try {
            const [attendance, expenses, pukes] = await Promise.all([
                axios.get(BASE_ENDPOINTS.attendance),
                axios.get(BASE_ENDPOINTS.expenses),
                axios.get(BASE_ENDPOINTS.pukes),
            ]);

            setBaseRankings({
                attendance: normalizeRankings(attendance.data),
                expenses: normalizeRankings(expenses.data),
                pukes: normalizeRankings(pukes.data),
            });
        } catch {
            setBaseError('Ranglijsten konden niet worden geladen. Probeer het later opnieuw.');
        } finally {
            setBaseLoading(false);
        }
    }, [axios]);

    const fetchDertigenRankings = useCallback(async (key: DertigenLeaderboardKey) => {
        setDertigenLoading(true);
        setDertigenError(null);

        try {
            const response = await axios.get(DERTIGEN_ENDPOINTS[key]);
            setDertigenRankings(normalizeRankings(response.data));
            setLoadedDertigen(prev => ({ ...prev, [key]: true }));
        } catch {
            setDertigenError('Dertigen ranglijst kon niet worden geladen. Probeer het later opnieuw.');
        } finally {
            setDertigenLoading(false);
        }
    }, [axios]);

    useEffect(() => {
        fetchBaseRankings();
    }, [fetchBaseRankings]);

    useEffect(() => {
        if (selectedLeaderboard !== 'dertigen') return;
        if (loadedDertigen[selectedDertigenLeaderboard]) return;
        fetchDertigenRankings(selectedDertigenLeaderboard);
    }, [selectedLeaderboard, selectedDertigenLeaderboard, loadedDertigen, fetchDertigenRankings]);

    const onPrimaryLeaderboardChange = (value: string | number | undefined) => {
        if (typeof value !== 'string') return;
        setSelectedLeaderboard(value as LeaderboardKey);
    };

    const onDertigenLeaderboardChange = (value: string | number | undefined) => {
        if (typeof value !== 'string') return;
        const next = value as DertigenLeaderboardKey;
        setSelectedDertigenLeaderboard(next);
        fetchDertigenRankings(next);
    };

    const isLoading = selectedLeaderboard === 'dertigen' ? dertigenLoading : baseLoading;
    const errorMessage = selectedLeaderboard === 'dertigen' ? dertigenError : baseError;

    const activeEntries = useMemo(() => {
        if (selectedLeaderboard === 'dertigen') return dertigenRankings;
        return baseRankings[selectedLeaderboard];
    }, [selectedLeaderboard, dertigenRankings, baseRankings]);

    const renderState = () => {
        if (isLoading) {
            return (
                <div className="loading-spinner-container">
                    <LoadingSpinner />
                </div>
            );
        }

        if (errorMessage) {
            return (
                <div className="leaderboard-state-panel">
                    <IonLabel>{errorMessage}</IonLabel>
                </div>
            );
        }

        if (activeEntries.length === 0) {
            return (
                <div className="leaderboard-state-panel">
                    <IonLabel>Nog geen data beschikbaar voor deze ranglijst.</IonLabel>
                </div>
            );
        }

        return (
            <IonList className="leaderboard-list">
                {activeEntries.map((entry, index) => (
                    <IonItem key={`${entry.name}-${entry.data}-${index}`} className="leaderboard-item" lines="none">
                        <div className="leaderboard-row">
                            <span className="leaderboard-medal">{medalForIndex(index)}</span>
                            <ProfilePicture
                                profilePicUrl={resolveProfilePicture(entry.profilePictureUrl)}
                                showEditable={false}
                                width={38}
                                height={38}
                                fullName={entry.name}
                            />
                            <div className="leaderboard-texts">
                                <span className="leaderboard-name">{entry.name}</span>
                                <span className="leaderboard-metric">
                                    {formatLeaderboardText(entry, selectedLeaderboard, selectedDertigenLeaderboard)}
                                </span>
                            </div>
                        </div>
                    </IonItem>
                ))}
            </IonList>
        );
    };


    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonMenuButton/>
                    </IonButtons>
                    <IonTitle>Ranglijsten</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen className="ranglijsten-content">
                <div className="ranglijsten-hero-wrapper">
                    <IonImg className="ranglijsten-hero" src="https://cdn-icons-png.flaticon.com/512/983/983763.png" />
                </div>

                <IonSegment
                    mode="ios"
                    scrollable={true}
                    className="leaderboard-primary-segment"
                    value={selectedLeaderboard}
                    onIonChange={e => onPrimaryLeaderboardChange(e.detail.value)}
                >
                    <IonSegmentButton value="attendance">
                        <IonLabel>Aanwezigheid</IonLabel>
                    </IonSegmentButton>
                    <IonSegmentButton value="expenses">
                        <IonLabel>Uitgegeven</IonLabel>
                    </IonSegmentButton>
                    <IonSegmentButton value="pukes">
                        <IonLabel>Barry's</IonLabel>
                    </IonSegmentButton>
                    <IonSegmentButton value="dertigen">
                        <IonLabel>Dertigen</IonLabel>
                    </IonSegmentButton>
                </IonSegment>

                {selectedLeaderboard === 'dertigen' && (
                    <IonSegment
                        mode="ios"
                        scrollable={true}
                        className="leaderboard-secondary-segment"
                        value={selectedDertigenLeaderboard}
                        onIonChange={e => onDertigenLeaderboardChange(e.detail.value)}
                    >
                        <IonSegmentButton value="stripe-goal">
                            <IonLabel>Strependoel</IonLabel>
                        </IonSegmentButton>
                        <IonSegmentButton value="doubler">
                            <IonLabel>Verdubbelaars</IonLabel>
                        </IonSegmentButton>
                        <IonSegmentButton value="dertigen-record">
                            <IonLabel>Record</IonLabel>
                        </IonSegmentButton>
                    </IonSegment>
                )}

                {renderState()}
            </IonContent>
        </IonPage>
    );
};

export default Ranglijsten;
